import { offerStore } from '@store/orders';
import { convertUtcToLocalAndFormat } from '@utils/dateFormat';
import { formatAmount } from '@utils/utils';
import { createSignal, For, Show } from 'solid-js';
import { userStore } from '@store/user';
import { PencilIcon } from '@atgfr8/icons';
import { Input } from '@components';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';

import {
  carrierStatusStyles,
  statusStyles,
  expireBid,
  counterAmountStyles,
  editBid,
  approveCounter,
  bookLoad,
  isLoadAvailable,
} from './helpers';
import { SubmitBid } from './SubmitBid';

export const MyOffers = (props: {
  changeTrackingSource?: ChangeTrackingSource;
}) => {
  const headerStyles =
    'font-semibold text-black text-sm py-2 sticky top-0 z-10 bg-white';
  const cellStyles = 'flex text-sm py-1 items-center';

  return (
    <div class="border border-armstrong-blue rounded-md">
      <div class="text-lg text-black font-semibold px-2 py-1 bg-[#e4edf4] rounded-t-md border-b border-armstrong-blue ">
        My Offers
      </div>
      <SubmitBid changeTrackingSource={props.changeTrackingSource} />
      <div class="grid bg-white rounded-b-md shadow-md text-black  grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_1fr_0.3fr] max-h-[420px] overflow-y-auto px-2">
        <div class={`${headerStyles} px-1 col-span-2`}>Carrier</div>
        <div class={headerStyles}>Request Date</div>
        <div class={headerStyles}>Status</div>
        <div class={headerStyles}>Bid Amount</div>
        <div class={`${headerStyles}`}>Counter</div>
        <div class={`${headerStyles} `}>Actions </div>
        <div class={headerStyles}>Live</div>
        <div class="col-span-8 h-[2px] bg-gray-300 my-px mx-1"></div>
        <For
          each={offerStore.bids.filter(
            (bid) => bid.userId === userStore.user.id,
          )}
        >
          {(offer) => {
            const [editable, setEditable] = createSignal(false);
            const [amount, setAmount] = createSignal(offer.amount);
            const disabled = () => {
              return (
                offer.status === 'Expired' ||
                offer.status === 'Booked' ||
                offer.status === 'Assigned' ||
                !isLoadAvailable()
              );
            };

            return (
              <div
                class={`col-span-8 grid grid-cols-subgrid border-b border-gray-300 items-center ${offer.status === 'Expired' ? ' opacity-40 text-gray-500 bg-gray-300' : ''}`}
              >
                <div
                  class={`${cellStyles} flex-col gap-1  col-span-2 items-start`}
                >
                  <a
                    href={`/carrier/details/${offer.carrierId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-armstrong-blue text-sm underline"
                  >
                    {offer.carrierName}
                  </a>
                  <div class="flex flex-wrap items-center gap-1 w-full min-w-0">
                    <div
                      class={`text-sm ${carrierStatusStyles[offer.carrierStatus]} rounded-xl px-2 py-px w-fit y-fit text-center`}
                    >
                      {offer.carrierStatus}
                    </div>
                    <div
                      class={
                        'text-sm rounded-xl px-2 py-px w-fit y-fit text-center bg-gray-200'
                      }
                    >
                      MC#{offer.mc}
                    </div>
                    <Show when={offer.bidType === 'External'}>
                      <div
                        class={
                          'text-sm rounded-xl px-2 py-px w-fit y-fit text-center text-armstrong-primary bg-[#daf2ff]'
                        }
                      >
                        Ext.
                      </div>
                    </Show>
                  </div>
                </div>
                <div class={cellStyles}>
                  {convertUtcToLocalAndFormat(
                    offer.createdDate,
                    'MM/dd/yyyy h:mm a',
                  )}
                </div>
                <div class={cellStyles}>
                  <div
                    class={`rounded-lg px-2 py-1 text-sm y-fit w-fit font-roboto  ${statusStyles[offer.status]}`}
                  >
                    {offer.status}
                  </div>
                </div>
                <div class={`${cellStyles} font-roboto gap-x-1`}>
                  <Show when={!editable()}>
                    <div>
                      {formatAmount(offer.amount, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </div>
                  </Show>
                  <Show when={!editable()}>
                    <PencilIcon
                      class={`text-armstrong-blue cursor-pointer ${disabled() ? 'opacity-50 pointer-events-none' : ''}`}
                      size={'16px'}
                      onClick={() => {
                        if (disabled()) return;
                        setEditable(true);
                      }}
                    />
                  </Show>
                  <Show when={editable()}>
                    <div class="w-3/4">
                      <Input
                        label=""
                        type="number"
                        value={amount()}
                        onInput={(e: { target: { value: string } }) => {
                          setAmount(Number(e.target.value));
                        }}
                        onKeyDown={async (e: { key: string }) => {
                          if (e.key === 'Enter') {
                            await editBid(
                              offer.id,
                              amount()!,
                              props.changeTrackingSource,
                            );
                            setEditable(false);
                          }
                        }}
                      />
                    </div>
                  </Show>
                </div>
                <div
                  class={`${cellStyles} ${counterAmountStyles(offer.counterAmount)} font-bold font-roboto`}
                >
                  {formatAmount(offer.counterAmount, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
                <div class={`${cellStyles} gap-x-2 `}>
                  <Show when={editable()}>
                    <button
                      class="bg-armstrong-blue text-white px-2 py-1 rounded-md text-sm"
                      onClick={() => {
                        if (amount() !== null) {
                          void editBid(
                            offer.id,
                            amount()!,
                            props.changeTrackingSource,
                          );
                          setEditable(false);
                        }
                      }}
                      disabled={disabled()}
                    >
                      RESUBMIT
                    </button>
                    <button
                      class="bg-[#EBEBEB] text-black px-2 py-1 rounded-md text-sm"
                      onClick={() => {
                        setEditable(false);
                      }}
                    >
                      CANCEL
                    </button>
                  </Show>
                  <Show when={offer.status === 'Countered' && !editable()}>
                    <button
                      class={`${statusStyles[offer.status]} px-2 py-1 rounded-md text-sm`}
                      onClick={async () => {
                        await approveCounter(
                          offer.id,
                          props.changeTrackingSource,
                        );
                      }}
                      disabled={disabled()}
                    >
                      ACCEPT
                    </button>
                  </Show>
                  <Show when={offer.status === 'Approved' && !editable()}>
                    <button
                      class={`${statusStyles['Countered']} text-white px-2 py-1 rounded-md text-sm shadow-[0_0_15px_rgba(250,204,21,0.7)] disabled:opacity-50 disabled:pointer-events-none`}
                      onClick={async () => {
                        await bookLoad(offer.id, props.changeTrackingSource);
                      }}
                      disabled={disabled()}
                    >
                      BOOK LOAD
                    </button>
                  </Show>
                </div>
                <div class={`${cellStyles} ml-2`}>
                  <input
                    type="checkbox"
                    class="accent-armstrong-blue hover:cursor-pointer disabled:pointer-events-none"
                    disabled={disabled()}
                    checked={!disabled()}
                    onChange={() => {
                      void expireBid(offer.id, props.changeTrackingSource);
                    }}
                  />
                </div>
              </div>
            );
          }}
        </For>
      </div>
    </div>
  );
};
